import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  WsBook,
  Trade,
  WsBbo,
  AllMids,
  WsLevel,
  Candle,
  WsActiveAssetCtx,
  WebData2,
  OrderFilled,
} from '@/lib/hyperliquid/types';
import type { UniverseItem, AssetContext, ActiveAssetData } from '@/api/hyperliquid/types/hyperliquid';

interface TokenAssetInfo {
  index: number;
  universe: UniverseItem;
  assetCtxs: AssetContext;
}

interface HyperliquidState {
  // Selected coin
  selectedAssetNumber: number; // Asset ID for API calls

  // Universe and Asset Contexts
  universe: UniverseItem[];
  universeMap: Map<string, number>; // symbol -> index (assetId)
  assetCtxs: AssetContext[];
  metaAndAssetCtxs: TokenAssetInfo[];
  activeAssetCtx: AssetContext | null;
  activeAssetData: ActiveAssetData | null;

  // Order Book
  orderBook: {
    coin: string;
    bids: WsLevel[];
    asks: WsLevel[];
    time: number;
  };

  // Best Bid/Offer
  bbo: {
    coin: string;
    bestBid: WsLevel | null;
    bestAsk: WsLevel | null;
    time: number;
  };

  // Trades
  trades: Trade[];
  maxTrades: number;

  // Order Fills
  orderFills: OrderFilled[];
  maxOrderFills: number;

  // All Mid Prices
  mids: Record<string, string>;

  // Candles
  candles: Record<string, Candle[]>; // Keyed by interval

  // Active Asset Context
  assetCtx: WsActiveAssetCtx | null;

  // Actions
  setSelectedAssetNumber: (assetNumber: number, ctx: AssetContext | null) => void;
  setUniverse: (universe: UniverseItem[], assetCtxs: AssetContext[]) => void;
  getAssetId: (symbol: string) => number;
  updateOrderBook: (data: WsBook) => void;
  updateBbo: (data: WsBbo) => void;
  addTrades: (trades: Trade[]) => void;
  addOrderFills: (fills: OrderFilled[]) => void;
  updateMids: (mids: Record<string, string>) => void;
  updateCandles: (interval: string, candles: Candle[]) => void;
  updateAssetCtx: (ctx: WsActiveAssetCtx) => void;
  updateAssetData: (data: ActiveAssetData) => void;
  setActiveAssetCtx: (ctx: AssetContext) => void;
  setActiveAssetData: (data: ActiveAssetData) => void;
  setMetaAndAssetCtxs: (universe: UniverseItem[], assetCtxs: AssetContext[]) => void;
  clearTrades: () => void;
  clearOrderFills: () => void;
}

export const useHyperliquidStore = create<HyperliquidState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    selectedAssetNumber: -1, // ETH asset ID

    // Universe data
    universe: [],
    universeMap: new Map(),
    assetCtxs: [],
    metaAndAssetCtxs: [],
    activeAssetCtx: null,
    activeAssetData: null,

    orderBook: {
      coin: 'BTC',
      bids: [],
      asks: [],
      time: Date.now(),
    },

    bbo: {
      coin: 'BTC',
      bestBid: null,
      bestAsk: null,
      time: Date.now(),
    },

    trades: [],
    maxTrades: 100,

    orderFills: [],
    maxOrderFills: 100,

    mids: {},

    candles: {},

    assetCtx: null,

    // Actions
    setSelectedAssetNumber: (assetNumber, ctx) => {
      set({
        selectedAssetNumber: assetNumber,
        activeAssetCtx: ctx,
      });
    },
    setActiveAssetData: (data: ActiveAssetData) => {
      set({ activeAssetData: data });
    },

    setUniverse: (universe, assetCtxs) => {
      const universeMap = new Map<string, number>();
      universe.forEach((asset, index) => {
        universeMap.set(asset.name, index);
      });
      set({ universe, universeMap, assetCtxs });
    },

    setMetaAndAssetCtxs: (universe, assetCtxs) => {
      set({
        metaAndAssetCtxs: universe.map((asset, index) => ({ index, universe: asset, assetCtxs: assetCtxs[index] })),
      });
    },

    getAssetId: symbol => {
      const state = get();
      return state.universeMap.get(symbol) ?? 0;
    },

    updateOrderBook: data => {
      set({
        orderBook: {
          coin: data.coin,
          bids: data.levels[0] || [],
          asks: data.levels[1] || [],
          time: data.time,
        },
      });
    },

    updateAssetData: data => {
      set({ activeAssetData: data });
    },

    updateBbo: data => {
      set({
        bbo: {
          coin: data.coin,
          bestBid: data.bbo[0],
          bestAsk: data.bbo[1],
          time: data.time,
        },
      });
    },

    addTrades: newTrades => {
      set(state => {
        return {
          trades: newTrades.concat(state.trades).slice(0, 100),
        };
      });
    },

    addOrderFills: newFills => {
      set(state => {
        return {
          orderFills: newFills.concat(state.orderFills).slice(0, state.maxOrderFills),
        };
      });
    },

    updateMids: mids => {
      set({ mids });
    },

    updateCandles: (interval, candles) => {
      set(state => ({
        candles: {
          ...state.candles,
          [interval]: candles,
        },
      }));
    },

    updateAssetCtx: ctx => {
      set({ assetCtx: ctx });
    },

    setActiveAssetCtx: ctx => {
      set({ activeAssetCtx: ctx });
    },

    clearTrades: () => {
      set({ trades: [] });
    },

    clearOrderFills: () => {
      set({ orderFills: [] });
    },
  })),
);

// Selectors
export const selectBids = (state: HyperliquidState) => state.orderBook.bids;
export const selectAsks = (state: HyperliquidState) => state.orderBook.asks;

export const selectSpread = (state: HyperliquidState) => {
  const bestBid = state.bbo.bestBid || state.orderBook.bids[0];
  const bestAsk = state.bbo.bestAsk || state.orderBook.asks[0];
  if (!bestBid || !bestAsk) return 0;
  return parseFloat(bestAsk.px) - parseFloat(bestBid.px);
};

export const selectSpreadPercent = (state: HyperliquidState) => {
  const bestBid = state.bbo.bestBid || state.orderBook.bids[0];
  const spread = selectSpread(state);
  if (!bestBid) return 0;
  const bidPrice = parseFloat(bestBid.px);
  return bidPrice > 0 ? (spread / bidPrice) * 100 : 0;
};

export const selectMidPrice = (state: HyperliquidState) => {
  const coin = 'BTC';
  const mid = state.mids[coin];
  if (mid) return parseFloat(mid);

  const bestBid = state.bbo.bestBid || state.orderBook.bids[0];
  const bestAsk = state.bbo.bestAsk || state.orderBook.asks[0];
  if (!bestBid || !bestAsk) return 0;
  return (parseFloat(bestBid.px) + parseFloat(bestAsk.px)) / 2;
};

export const selectMarkPrice = (state: HyperliquidState) => {
  if (state.assetCtx?.ctx && typeof state.assetCtx.ctx.markPx === 'number') {
    return state.assetCtx.ctx.markPx;
  }
  if (state.assetCtx?.ctx && typeof state.assetCtx.ctx.markPx === 'string') {
    return parseFloat(state.assetCtx.ctx.markPx);
  }
  return selectMidPrice(state);
};

export const selectFundingRate = (state: HyperliquidState) => {
  if (state.assetCtx?.ctx && 'funding' in state.assetCtx.ctx) {
    return state.assetCtx.ctx.funding;
  }
  return 0;
};

export const selectOpenInterest = (state: HyperliquidState) => {
  if (state.assetCtx?.ctx && 'openInterest' in state.assetCtx.ctx) {
    return state.assetCtx.ctx.openInterest;
  }
  return 0;
};

export const selectVolume24h = (state: HyperliquidState) => {
  if (state.assetCtx?.ctx) {
    return state.assetCtx.ctx.dayNtlVlm;
  }
  return 0;
};
